import { cloneElement, type Attributes, type FC, type ReactElement, type ReactNode } from 'react';

export const isSlot = (node: ReactNode, slot: FC) => {
  return (node as ReactElement).type === slot;
};

interface GetSlot {
  (slot: FC, children: ReactNode | ReactNode[]): ReactElement[] | null;
  (slot: FC, children: ReactNode | ReactNode[], index: number): ReactElement | null;
}

export const getSlot = ((slot: FC, children: ReactNode | ReactNode[], index?: number) => {
  if (!children) {
    return null;
  }

  const nodes = Array.isArray(children) ? children : [children];
  if (index !== undefined) {
    return addKey(nodes.filter((child) => isSlot(child, slot))[index]);
  }

  return addKey(nodes.filter((child) => isSlot(child, slot)));
}) as GetSlot;

interface NotSlot {
  (slot: FC, children: ReactNode | ReactNode[]): ReactElement[] | null;
  (slot: FC, children: ReactNode | ReactNode[], index: number): ReactElement | null;
}

export const notSlot = ((slot: FC, children: ReactNode | ReactNode[], index?: number) => {
  if (!children) {
    return null;
  }

  const nodes = Array.isArray(children) ? children : [children];
  if (index !== undefined) {
    return addKey(nodes.filter((child) => !isSlot(child, slot))[index]);
  }

  return addKey(nodes.filter((child) => !isSlot(child, slot)));
}) as NotSlot;

export const injectInSlotInstance = <P extends Partial<unknown> & Attributes>(element: ReactElement | ReactElement[] | null | undefined, props: P) => {
  if (!element) {
    return null;
  }

  const array = Array.isArray(element) ? element : [element];
  return array.map(element => cloneElement(element, props));
};

function addKey(element: ReactElement | ReactElement[] | ReactNode | ReactNode[] | null) {
  if (!element) {
    return null;
  }

  if (!Array.isArray(element)) {
    return element;
  }

  return element.map((element, index) => cloneElement((element as ReactElement), { key: index }));
}
